/* eslint-disable no-unused-vars */
import { useState } from "react";
import { Box, Grid, Typography, makeStyles } from "@material-ui/core";
import { colours } from "../constants/theme";
import { ButtonPrimary, ButtonTextPrimary } from "../components-atoms";
import { CourierPriceResult, ICourierVehicleType } from "../redux/reducers/orderDetails";
import { VEHICLE_TYPES } from "../utils/vehicleAssetUtil";
import { useTranslation } from "react-i18next";

export interface IAvailableCourierItemProps {
  Id: string;
  Name: string;
  Logo: string;
  Price: CourierPriceResult;
  // ServiceType: ICourierServiceType;
  VehicleType: ICourierVehicleType;
  onPremierAccept?: (e: string) => void;
  onInfoClick?: () => void;
  hasError?: boolean;
  isPremier?: boolean;
  disabled: boolean;
  isMaxPrice?: boolean;           // indicates if this quote is set as max price
  isBelowMaxPrice?: boolean;      // indicates if this quote is below the max price threshold
  onSetMaxPrice?: (e: string) => void;    // callback for setting max price
  onUnsetMaxPrice?: (e: string) => void;  // callback for unsetting max price
}

const useStyles = makeStyles(() => ({
  imageAndNameContainer: {
    display: 'flex',
    gap: 16,
  },
  name: {
    lineBreak: 'anywhere'
  },
  image: {
    borderRadius: 4,
    border: `1px solid ${colours.dark12}`,
    objectFit: 'contain'
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  serviceTypeContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    gap: 8,
    '& > .MuiTypography-caption': {
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'capitalize'
    }
  },
  serviceTypeIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: colours.dark08,
    '& > svg': {
      width: 18,
      height: 18,
    }
  },
  vehicleTypeIcon: {
    '& > img': {
      width: 24,
      height: 24,
      filter: 'invert(1)'
    }
  },
  infoButton: {
    width: 40,
    height: 40,
  },
  container: {
    margin: '0 -16px',
    padding: '16px',
    borderRadius: 8,
  },
  belowMaxPrice: {
    backgroundColor: `${colours.primary}10`, // faded primary color
  },
  maxPriceSelected: {
    // border: `1px solid ${colours.primary}`,
  }
}));

export default function AvailableCourierItem(props: IAvailableCourierItemProps) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [conciergeLoading, setConciergeLoading] = useState(false);
  const {
    Id,
    Name,
    Logo,
    Price,
    VehicleType,
    hasError,
    onPremierAccept,
    isPremier,
    disabled,
    isMaxPrice,
    isBelowMaxPrice,
    onSetMaxPrice,
    onUnsetMaxPrice
  } = props;

  // No need to show them with Nash.
  // const getServiceIcon = (serviceType: ICourierServiceType) => {
  //   switch (serviceType) {
  //     case 'direct':
  //       return <FlashOnRoundedIcon htmlColor="#edc700" />;
  //     default:
  //       return <SwapCallsRoundedIcon htmlColor={colours.primary} />;
  //   }
  // }

  // const getServiceName = (serviceType: ICourierServiceType) => {
  //   switch (serviceType) {
  //     case 'regular':
  //       return "Standard";
  //     case 'direct':
  //       return "Priority";
  //     default:
  //       return serviceType;
  //   }
  // }

  // const renderInfoButton = () => {
  //   return (
  //     <IconButton className={classes.infoButton} onClick={onInfoClick}>
  //       <svg
  //         xmlns="http://www.w3.org/2000/svg"
  //         width={16}
  //         height={16}
  //         fill="none">
  //         <path
  //           fill="currentColor"
  //           d="M8 4.667a1 1 0 1 0 0-2 1 1 0 0 0 0 2ZM7.333 6a.667.667 0 1 0 0 1.333v5.334a.667.667 0 1 0 1.334 0v-6A.667.667 0 0 0 8 6h-.667Z"
  //         />
  //       </svg>
  //     </IconButton>
  //   )
  // }

  return (
    <Grid
      container
      key={Id}
      className={`${classes.container} ${isBelowMaxPrice ? classes.belowMaxPrice : ''} ${isMaxPrice ? classes.maxPriceSelected : ''}`}
    >
      <Grid container item xs={4} className={classes.imageAndNameContainer}>
        <Grid item>
          <img width={64} height={64} src={Logo} alt={`${Name} logo`} className={classes.image} />
        </Grid>
        <Grid item xs>
          <Typography className={classes.name}>{Name}</Typography>
          {(VehicleType && VehicleType?.Code?.length > 0) &&
            <Box className={classes.serviceTypeContainer}>
              <Box className={`${classes.serviceTypeIcon} ${classes.vehicleTypeIcon}`}>
                <img
                  key={VehicleType.Code}
                  alt={VehicleType.Name}
                  draggable="false"
                  src={VEHICLE_TYPES[VehicleType.Code]?.image}
                />
              </Box>
              <Typography
                variant="caption"
                color="textPrimary">
                {VehicleType.Name}
              </Typography>
            </Box>
          }

          {/* {(ServiceType && ServiceType.length > 0) &&
            <Box className={classes.serviceTypeContainer}>
              <Box className={classes.serviceTypeIcon}>
                {getServiceIcon(ServiceType)}
              </Box>
              {ServiceType === 'direct' &&
                <Box className={classes.serviceTypeIcon}>
                  {renderInfoButton()}
                </Box>
              }
              <Typography
                variant="caption"
                color="textPrimary">
                {getServiceName(ServiceType)}
              </Typography>
            </Box>
          } */}
        </Grid>
      </Grid>
      {/* Pricing */}
      <Grid container item xs={5} style={{ marginLeft: 24 }}>
        <Grid container item xs direction="column" spacing={1}>
          {/* Delivery Fee */}
          <Grid container item>
            <Grid container item xs={8}>
              <Typography
                variant="caption"
                color="textPrimary"
                style={{ fontWeight: 400, paddingLeft: 48 }}>
                Delivery fee
              </Typography>
            </Grid>
            <Grid xs item container justifyContent="flex-end">
              <Typography variant="body2" component="p" color="textPrimary">
{t('currencySymbol')}{(Price.Min.Delivery / 100).toFixed(2)}
                {Price.Max && Price.Max.Delivery && ` - ${t('currencySymbol')}${(Price.Max.Delivery / 100).toFixed(2)}`}
              </Typography>
            </Grid>
          </Grid>
          <Grid container item>
            <Grid container item xs={8}>
              <Typography
                variant="caption"
                color="textPrimary"
                style={{ fontWeight: 400, paddingLeft: 48 }}>
                Booking fee
              </Typography>
            </Grid>
            <Grid xs item container justifyContent="flex-end">
              {Price.Min.BookingFee?.CreditUsage && (
                <Typography variant="body2" component="p" color="textPrimary">
                  {`${Price.Min.BookingFee.CreditUsage} credit`}
                </Typography>
              )}
              {Price.Min.BookingFee?.PayAsYouGoPrice && (
                <Typography variant="body2" component="p" color="textPrimary">
{`${t('currencySymbol')}${(Price.Min.BookingFee.PayAsYouGoPrice / 100).toFixed(2)}`}
                </Typography>
              )}
            </Grid>
          </Grid>
          {Price.Min.PremiumFee && (
            <Grid container item>
              <Grid container item xs={8}>
                <Typography
                  variant="caption"
                  color="textPrimary"
                  style={{ fontWeight: 400, paddingLeft: 48 }}>
                  Concierge fee
                </Typography>
              </Grid>
              <Grid xs item container justifyContent="flex-end">
                <Typography variant="body2" component="p" color="textPrimary">
{t('currencySymbol')}{(Price.Min.PremiumFee / 100).toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          )}
          {/* Delivery Fee */}
          <Grid container item>
            <Grid container item xs={6}>
              <Typography
                variant="caption"
                color="textPrimary"
                style={{ fontWeight: 400, paddingLeft: 48 }}>
                VAT
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" component="p" color="textPrimary" align="right">
{t('currencySymbol')}{(Price.Min.Vat / 100).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
          {/* Order Total */}
          <Grid container item xs style={{ fontWeight: 400, paddingLeft: 48 }}>
            <Grid container item xs>
              <Typography variant="body2" color="textPrimary" style={{ fontWeight: 700 }}>
                Total
              </Typography>
            </Grid>
            <Grid xs item container justifyContent="flex-end">
              <Typography
                variant="body2"
                component="p"
                color="textPrimary"
                style={{ fontWeight: 700 }}>
{t('currencySymbol')}{(Price.Min.Total / 100).toFixed(2)}
                {Price.Max && Price.Max.Total && ` - ${t('currencySymbol')}${(Price.Max.Total / 100).toFixed(2)}`}
              </Typography>
            </Grid>
          </Grid>
          {/* Order Total */}
        </Grid>
      </Grid>
      {/* Pricing */}
      <Grid item xs className={classes.buttonContainer}>
        {isMaxPrice ? (
          <ButtonPrimary
            disabled={hasError || disabled}
            onClick={() => onUnsetMaxPrice?.(Id)}
          >
            Unset max price
          </ButtonPrimary>
        ) : isPremier ? (
          <ButtonTextPrimary
            disabled={conciergeLoading || hasError || disabled}
            onClick={() => {
              setConciergeLoading(true);
              onPremierAccept?.(Id);
            }}>
            Book concierge
          </ButtonTextPrimary>
        ) : (
          <ButtonTextPrimary
            disabled={hasError || disabled}
            onClick={() => onSetMaxPrice?.(Id)}
          >
            Set as max price
          </ButtonTextPrimary>
        )}
      </Grid>
    </Grid>
  );
}
